// src/components/Statistics.js
import React, { useEffect, useState } from 'react';
import { getStatistics } from '../api/statisticsApi';
import {
    Container,
    Typography,
    Box,
    Button,
    ButtonGroup,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper
} from '@mui/material';

import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Registreerime Chart.js moodulid ja pluginad
ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    ChartDataLabels
);

export default function Statistics({ userId, affiliateId }) {
    const [stats, setStats] = useState(null);
    const [homeAffiliateTrainingMode, setHomeAffiliateTrainingMode] = useState('all');
    const [childrenHomeAffiliateTrainingMode, setChildrenHomeAffiliateTrainingMode] = useState({});
    const [selectedMonthYear, setSelectedMonthYear] = useState('');

    useEffect(() => {
        const params = {};
        if (userId) params.userId = userId;
        if (affiliateId) params.affiliateId = affiliateId;

        async function fetchData() {
            const data = await getStatistics(params);
            setStats(data);
        }
        fetchData();
    }, [userId, affiliateId]);

    useEffect(() => {
        if (!stats || selectedMonthYear) return;

        const availableYears = (stats.monthlyTrainingsByYear || [])
            .map((item) => item.year)
            .sort((a, b) => b - a);
        const fallbackYear = availableYears[0] || (stats.yearlyTrainings?.[0]?.year ?? '');

        if (fallbackYear) {
            setSelectedMonthYear(String(fallbackYear));
        }
    }, [stats, selectedMonthYear]);

    useEffect(() => {
        if (!stats) return;

        const childrenStats = stats.homeAffiliateChildrenYearlyTrainings || [];
        setChildrenHomeAffiliateTrainingMode((prev) => {
            const next = { ...prev };
            childrenStats.forEach((child) => {
                if (!next[child.familyMemberId]) {
                    const hasCheckIn = (child.yearlyTrainings || []).length > 0;
                    const hasAll = (child.yearlyTrainingsAll || []).length > 0;
                    next[child.familyMemberId] = hasAll ? 'all' : (hasCheckIn ? 'checkIn' : 'all');
                }
            });
            return next;
        });
    }, [stats]);

    if (!stats) {
        return (
            <Container>
                <Typography>Loading Statistics...</Typography>
            </Container>
        );
    }

    // Destructure serverist tulevad andmed
    const {
        trainingTypeCounts,
        monthlyTrainings,
        monthlyTrainingsByYear,
        yearlyTrainings,
        yearlyScores,
        homeAffiliateName,
        homeAffiliateYearlyTrainings,
        homeAffiliateYearlyTrainingsAll,
        homeAffiliateChildrenYearlyTrainings,
    } = stats;

    // Function to get color based on training type
    const getTrainingTypeColor = (type, isHover = false) => {
        const colors = {
            'WOD': { regular: '#1976d2', hover: '#2196f3' },
            'Weightlifting': { regular: '#4caf50', hover: '#66bb6a' },
            'Cardio': { regular: '#ff9800', hover: '#ffc107' },
            'Rowing': { regular: '#9c27b0', hover: '#ba68c8' },
            'Gymnastics': { regular: '#e91e63', hover: '#f06292' },
            'Other': { regular: '#607d8b', hover: '#78909c' }
        };

        if (colors[type]) {
            return isHover ? colors[type].hover : colors[type].regular;
        }

        // Default color for any types not in our list
        return isHover ? '#78909c' : '#607d8b';
    };

    // 1) Ringdiagramm (treeningute jaotus: WOD, Weightlifting, Cardio, Rowing, Gymnastics, Other)
    const doughnutData = {
        labels: Object.keys(trainingTypeCounts),
        datasets: [
            {
                data: Object.values(trainingTypeCounts),
                backgroundColor: Object.keys(trainingTypeCounts).map(type => getTrainingTypeColor(type)),
                hoverBackgroundColor: Object.keys(trainingTypeCounts).map(type => getTrainingTypeColor(type, true)),
            },
        ],
    };
    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom' },
            datalabels: {
                color: '#ffffff',
                font: {
                    weight: 'bold',
                    size: 14
                },
                formatter: (value) => {
                    return value; // Kuvab arvud
                }
            }
        },
    };

    // 2) Tabel - treeningud kuude lõikes (aasta filtriga)
    const availableMonthYears = (monthlyTrainingsByYear || [])
        .map((item) => item.year)
        .sort((a, b) => b - a);
    const fallbackYear = availableMonthYears[0] || (yearlyTrainings?.[0]?.year ?? '');
    const activeMonthYear = selectedMonthYear || fallbackYear;
    const selectedYearMonths = (monthlyTrainingsByYear || []).find((item) => item.year === Number(activeMonthYear))?.months
        || [];
    const sortedMonthly = [...selectedYearMonths].sort((a, b) => a.month - b.month);

    // 3) Tulbagraafik - treeningud aastate lõikes
    const barDataTrainings = {
        labels: yearlyTrainings.map((item) => item.year),
        datasets: [
            {
                label: 'Trainings per Year',
                data: yearlyTrainings.map((item) => item.count),
                backgroundColor: '#3f51b5',
                maxBarThickness: 50,
            },
        ],
    };

    // 4) Tulbagraafik - leaderboard sisestuste arv aastate lõikes
    const barDataScores = {
        labels: yearlyScores.map((item) => item.year),
        datasets: [
            {
                label: 'Leaderboard Entries',
                data: yearlyScores.map((item) => item.score),
                backgroundColor: '#e91e63',
                maxBarThickness: 50,
            },
        ],
    };

    // 5) HomeAffiliate treeningute arv (checkIn = true) aastate lõikes
    const selectedHomeAffiliateTrainings = homeAffiliateTrainingMode === 'checkIn'
        ? (homeAffiliateYearlyTrainings || [])
        : (homeAffiliateYearlyTrainingsAll || []);

    const barDataHomeAffiliate = {
        labels: selectedHomeAffiliateTrainings.map(item => item.year),
        datasets: [
            {
                label: homeAffiliateTrainingMode === 'checkIn'
                    ? `Checked-in trainings at ${homeAffiliateName || ''}`
                    : `All trainings at ${homeAffiliateName || ''}`,
                data: selectedHomeAffiliateTrainings.map(item => item.count),
                backgroundColor: '#009688',
                maxBarThickness: 50,
            },
        ],
    };

    // Ühised tulbagraafiku seaded
    const barOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true },
            datalabels: {
                display: true,
                color: '#ffffff',
                anchor: 'end',
                align: 'start',
                font: {
                    weight: 'bold',
                    size: 12,
                },
            },
        },
        scales: {
            x: { grid: { display: false } },
            y: {
                beginAtZero: true,
            },
        },
    };

    return (
        <Container
            maxWidth={false}
            sx={{
                px: { xs: 2, sm: 4, md: 6 },
                pb: { xs: 10, sm: 8, md: 6 },
            }}
        >
            <Box my={4}>
                <Typography variant="h5" gutterBottom>
                    Statistics
                </Typography>
            </Box>

            {/* RINGDIAGRAMM - Treeningutüübid */}
            <Box
                mb={4}
                sx={{
                    width: '100%',
                    maxWidth: 600,
                    mx: 'auto',
                    height: 400,
                    position: 'relative',
                }}
            >
                <Typography variant="h6" gutterBottom>
                    Training Type Distribution (Overall)
                </Typography>
                <Doughnut data={doughnutData} options={doughnutOptions} />
            </Box>

            {/* TABEL - treeningud kuude lõikes */}
            <Box
                mb={4}
                sx={{ maxWidth: 600, mx: 'auto', pt: 5 }}
            >
                <Typography variant="h6" gutterBottom>
                    Trainings by Month
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Trainings in this table are taken from the user's training diary.
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150, mb: 2 }}>
                    <InputLabel id="month-year-select-label">Year</InputLabel>
                    <Select
                        labelId="month-year-select-label"
                        value={activeMonthYear ? String(activeMonthYear) : ''}
                        label="Year"
                        onChange={(e) => setSelectedMonthYear(e.target.value)}
                    >
                        {availableMonthYears.map((year) => (
                            <MenuItem key={year} value={String(year)}>
                                {year}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Paper>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Month</TableCell>
                                <TableCell>Count</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedMonthly.length > 0 ? (
                                sortedMonthly.map((item) => (
                                    <TableRow key={`${activeMonthYear}-${item.month}`}>
                                        <TableCell>{item.month}</TableCell>
                                        <TableCell>{item.count}</TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={2}>No training diary entries for selected year.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Paper>
            </Box>

            {/* TULBAD - treeningud aastate lõikes */}
            <Box
                mb={4}
                sx={{
                    width: '100%',
                    maxWidth: 600,
                    mx: 'auto',
                    height: 400,
                    position: 'relative',
                    pt: 4,
                }}
            >
                <Typography variant="h6" gutterBottom>
                    Trainings by Year
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Trainings in this chart are taken from the user's training diary.
                </Typography>
                <Bar data={barDataTrainings} options={barOptions} />
            </Box>

            {/* TULBAD - leaderboard sisestuste arv aastate lõikes */}
            <Box
                mb={4}
                sx={{
                    width: '100%',
                    maxWidth: 600,
                    mx: 'auto',
                    height: 400,
                    position: 'relative',
                    pt: 4,
                }}
            >
                <Typography variant="h6" gutterBottom>
                    Leaderboard Entries by Year
                </Typography>
                <Bar data={barDataScores} options={barOptions} />
            </Box>

            {/* HOME AFFILIATE TULBAD - kui kasutajal on homeAffiliate */}
            {homeAffiliateName && (homeAffiliateYearlyTrainings?.length > 0 || homeAffiliateYearlyTrainingsAll?.length > 0) && (
                <Box
                    mb={4}
                    sx={{
                        width: '100%',
                        maxWidth: 600,
                        mx: 'auto',
                        pt: 4,
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Total trainings at {homeAffiliateName}
                    </Typography>
                    <ButtonGroup size="small" sx={{ mb: 2 }}>
                        <Button
                            variant={homeAffiliateTrainingMode === 'checkIn' ? 'contained' : 'outlined'}
                            onClick={() => setHomeAffiliateTrainingMode('checkIn')}
                        >
                            Check-In
                        </Button>
                        <Button
                            variant={homeAffiliateTrainingMode === 'all' ? 'contained' : 'outlined'}
                            onClick={() => setHomeAffiliateTrainingMode('all')}
                        >
                            All
                        </Button>
                    </ButtonGroup>
                    <Box sx={{ height: 300, position: 'relative' }}>
                        <Bar data={barDataHomeAffiliate} options={barOptions} />
                    </Box>
                </Box>
            )}

            {homeAffiliateName && (homeAffiliateChildrenYearlyTrainings || []).some((childStats) =>
                (childStats.yearlyTrainings || []).length > 0 || (childStats.yearlyTrainingsAll || []).length > 0
            ) && (
                <Box sx={{ maxWidth: 760, mx: 'auto', mb: 4, mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Children total trainings at {homeAffiliateName}
                    </Typography>
                    {(homeAffiliateChildrenYearlyTrainings || []).map((childStats) => {
                        const childMode = childrenHomeAffiliateTrainingMode[childStats.familyMemberId] || 'checkIn';
                        const hasCheckIn = (childStats.yearlyTrainings || []).length > 0;
                        const hasAll = (childStats.yearlyTrainingsAll || []).length > 0;

                        if (!hasCheckIn && !hasAll) {
                            return null;
                        }

                        const selectedChildTrainings = childMode === 'checkIn'
                            ? (childStats.yearlyTrainings || [])
                            : (childStats.yearlyTrainingsAll || []);

                        const fallbackChildTrainings = hasAll
                            ? (childStats.yearlyTrainingsAll || [])
                            : (childStats.yearlyTrainings || []);
                        const chartTrainings = selectedChildTrainings.length > 0
                            ? selectedChildTrainings
                            : fallbackChildTrainings;

                        const barDataChildHomeAffiliate = {
                            labels: chartTrainings.map((item) => item.year),
                            datasets: [
                                {
                                    label: childMode === 'checkIn'
                                        ? `${childStats.childName}: checked-in trainings at ${homeAffiliateName || ''}`
                                        : `${childStats.childName}: all trainings at ${homeAffiliateName || ''}`,
                                    data: chartTrainings.map((item) => item.count),
                                    backgroundColor: '#7b1fa2',
                                    maxBarThickness: 50,
                                },
                            ],
                        };

                        return (
                            <Box
                                key={childStats.familyMemberId}
                                sx={{
                                    width: '100%',
                                    maxWidth: 600,
                                    mx: 'auto',
                                    mb: 4,
                                    p: 2,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 2,
                                }}
                            >
                                <Typography variant="subtitle1" gutterBottom>
                                    {childStats.childName}
                                </Typography>
                                <ButtonGroup size="small" sx={{ mb: 2 }}>
                                    <Button
                                        variant={childMode === 'checkIn' ? 'contained' : 'outlined'}
                                        onClick={() =>
                                            setChildrenHomeAffiliateTrainingMode((prev) => ({
                                                ...prev,
                                                [childStats.familyMemberId]: 'checkIn',
                                            }))
                                        }
                                    >
                                        Check-In
                                    </Button>
                                    <Button
                                        variant={childMode === 'all' ? 'contained' : 'outlined'}
                                        onClick={() =>
                                            setChildrenHomeAffiliateTrainingMode((prev) => ({
                                                ...prev,
                                                [childStats.familyMemberId]: 'all',
                                            }))
                                        }
                                    >
                                        All
                                    </Button>
                                </ButtonGroup>
                                <Box sx={{ height: 300, position: 'relative' }}>
                                    <Bar data={barDataChildHomeAffiliate} options={barOptions} />
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Container>
    );
}